import posthog from 'posthog-js';

type AnalyticsProperties = Record<string, any>;

type AnalyticsUser = {
  id?: string;
  email?: string;
  name?: string;
  type?: string;
  umsId?: string;
};

let analyticsEnabled = false;
const activeReplaySources = new Set<string>();
const replayTimeouts = new Map<string, number>();
const SELECTIVE_REPLAY_MAX_MS = 10 * 60 * 1000;

const getCurrentRoute = () =>
  `${window.location.pathname}${window.location.search}${window.location.hash}`;

const safelyCallPostHog = (callback: () => void) => {
  try {
    callback();
  } catch {
    // Analytics should never block the workflow that triggered it.
  }
};

export const getAnalyticsAppEnv = () =>
  window.location.hostname === 'localhost' ||
  window.location.hostname === '127.0.0.1'
    ? 'localhost'
    : process.env.APP_ENV || 'development';

export const getAnalyticsBuildSha = () =>
  process.env.REACT_APP_BUILD_SHA || 'dev';

export const setAnalyticsEnabled = (enabled: boolean) => {
  analyticsEnabled = enabled;
};

export const track = (
  event: string,
  properties: AnalyticsProperties = {},
): void => {
  if (!analyticsEnabled) return;
  safelyCallPostHog(() => {
    posthog.capture(event, {
      route: getCurrentRoute(),
      ...properties,
    });
  });
};

export const identifyUser = (user: AnalyticsUser) => {
  if (!analyticsEnabled) return;
  const distinctId = user.umsId || user.id || user.email;
  if (!distinctId) return;

  safelyCallPostHog(() => {
    posthog.identify(distinctId, {
      email: user.email,
      name: user.name,
      user_type: user.type,
      ums_id: user.umsId,
    });
  });
};

export const resetAnalyticsUser = () => {
  if (!analyticsEnabled) return;
  safelyCallPostHog(() => {
    posthog.reset();
  });
  replayTimeouts.forEach(timeoutId => window.clearTimeout(timeoutId));
  replayTimeouts.clear();
  activeReplaySources.clear();
};

export const startSelectiveSessionReplay = (source: string) => {
  if (!analyticsEnabled || !source) return;

  const shouldStart = activeReplaySources.size === 0;
  activeReplaySources.add(source);

  const existingTimeout = replayTimeouts.get(source);
  if (existingTimeout) {
    window.clearTimeout(existingTimeout);
  }
  replayTimeouts.set(
    source,
    window.setTimeout(() => {
      stopSelectiveSessionReplay(source);
    }, SELECTIVE_REPLAY_MAX_MS),
  );

  if (shouldStart) {
    safelyCallPostHog(() => {
      posthog.startSessionRecording();
    });
  }
};

export const stopSelectiveSessionReplay = (source?: string) => {
  if (!analyticsEnabled) return;

  if (source) {
    activeReplaySources.delete(source);
    const timeoutId = replayTimeouts.get(source);
    if (timeoutId) {
      window.clearTimeout(timeoutId);
      replayTimeouts.delete(source);
    }
  } else {
    replayTimeouts.forEach(timeoutId => window.clearTimeout(timeoutId));
    replayTimeouts.clear();
    activeReplaySources.clear();
  }

  if (activeReplaySources.size === 0) {
    safelyCallPostHog(() => {
      posthog.stopSessionRecording();
    });
  }
};

export const commonPodProps = (pod: any = {}) => ({
  pod_name: pod?.name,
  pod_region_code: pod?.metadata?.regionCode,
  pod_osv: pod?.metadata?.osv,
  pod_server: pod?.metadata?.server,
  pod_tv_country: pod?.metadata?.tvCountry,
  pod_make: pod?.metadata?.make,
  vpn_provider: pod?.metadata?.vpnProvider,
});

export const describeError = (error: any) =>
  String(
    error?.data?.error ||
      error?.data?.message ||
      error?.message ||
      error?.error ||
      'unknown',
  );

export const getWorkflowDurationSeconds = (startedAt?: number | null) => {
  if (!startedAt) return undefined;
  return Math.max(0, Math.round((Date.now() - startedAt) / 1000));
};

export const getAssetProperties = ({
  file,
  dimensions,
  assetRole,
}: {
  file: File | null;
  dimensions?: { width?: number; height?: number; duration?: number } | null;
  assetRole: string;
}) => {
  const fileName = file?.name || '';
  const lastDotIndex = fileName.lastIndexOf('.');
  const fileExtension =
    lastDotIndex > -1 && lastDotIndex < fileName.length - 1
      ? fileName.slice(lastDotIndex + 1).toLowerCase()
      : undefined;

  return {
    asset_role: assetRole,
    file_extension: fileExtension,
    mime_type: file?.type,
    size_kb: file ? Math.round(file.size / 1024) : undefined,
    width: dimensions?.width,
    height: dimensions?.height,
    duration_seconds: dimensions?.duration,
  };
};
